import { Platform, Share, Alert } from "react-native";
import * as Print from "expo-print";
import * as Sharing from "expo-sharing";
import { MemoryModel, MediaModel, formatLocation, escapeHtml } from "common247";

export interface IExportPdfResult {
  uri: string;
  fileName: string;
}

/**
 * Generates an archival-grade HTML template for a Memory.
 * Employs heirloom serif typography, balanced margins, and photo framing.
 *
 * @param {MemoryModel} memory The memory item to render into PDF HTML.
 * @returns {string} Formatted HTML document string.
 */
export function buildMemoryPdfHtml(memory: MemoryModel): string {
  const safeTitle = escapeHtml(memory.title || "Untitled Memory");
  const safeDate = escapeHtml(memory.date || "Undated");
  const safeLocation = escapeHtml(formatLocation(memory.location));
  const safeOwner = escapeHtml(memory.ownerDisplayName || "Memories Contributor");
  const safeDescription = escapeHtml(memory.description || "")
    .split("\n")
    .map((line) => `<p>${line || "&nbsp;"}</p>`)
    .join("");

  const mediaList = memory.mediaItems || [];
  const mediaImagesHtml = mediaList
    .filter((item) => Boolean(item.downloadUrl))
    .map((item, idx) => `
      <div class="photo-card">
        <img src="${item.downloadUrl}" alt="${escapeHtml(item.name || `Memory photo ${idx + 1}`)}" crossorigin="anonymous" />
      </div>
    `)
    .join("");

  const exportDate = new Date().toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return `
    <!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="utf-8" />
      <title>${safeTitle}</title>
      <style>
        @page {
          size: A4;
          margin: 20mm;
        }
        * {
          box-sizing: border-box;
          margin: 0;
          padding: 0;
        }
        body {
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
          color: #262320;
          background: #FAF8F5;
          padding: 24px;
          line-height: 1.6;
        }
        .archival-frame {
          border: 1px solid #D6CEBE;
          background: #FFFFFF;
          padding: 36px 40px;
          position: relative;
        }
        .header-seal {
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-bottom: 2px solid #5C768D;
          padding-bottom: 12px;
          margin-bottom: 28px;
        }
        .seal-title {
          font-size: 11px;
          letter-spacing: 2px;
          font-weight: 700;
          color: #5C768D;
          text-transform: uppercase;
        }
        .seal-badge {
          font-size: 10px;
          color: #8C827A;
          letter-spacing: 1px;
        }
        .memory-title {
          font-family: Georgia, "Times New Roman", serif;
          font-size: 28px;
          font-weight: 700;
          color: #1F1C18;
          line-height: 1.25;
          margin-bottom: 14px;
        }
        .meta-pills {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          margin-bottom: 24px;
          padding-bottom: 16px;
          border-bottom: 1px dashed #E2DACD;
        }
        .pill {
          display: inline-flex;
          align-items: center;
          font-size: 12px;
          color: #574F46;
          background: #F4EFE6;
          padding: 4px 10px;
        }
        .narrative {
          font-family: Georgia, "Times New Roman", serif;
          font-size: 15px;
          color: #332E27;
          line-height: 1.8;
          margin-bottom: 32px;
        }
        .narrative p {
          margin-bottom: 12px;
        }
        .media-heading {
          font-size: 13px;
          font-weight: 700;
          letter-spacing: 1px;
          text-transform: uppercase;
          color: #5C768D;
          margin-bottom: 16px;
        }
        .gallery-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          gap: 16px;
          margin-bottom: 32px;
        }
        .photo-card {
          border: 1px solid #E0D7C8;
          background: #FAF8F5;
          padding: 8px;
          page-break-inside: avoid;
        }
        .photo-card img {
          width: 100%;
          height: 180px;
          object-fit: cover;
          display: block;
        }
        .footer-stamp {
          border-top: 1px solid #D6CEBE;
          padding-top: 16px;
          margin-top: 36px;
          display: flex;
          justify-content: space-between;
          font-size: 10px;
          color: #8C827A;
        }
      </style>
    </head>
    <body>
      <div class="archival-frame">
        <div class="header-seal">
          <span class="seal-title">Memories • Archival Record</span>
          <span class="seal-badge">PRESERVED ARTIFACT</span>
        </div>

        <h1 class="memory-title">${safeTitle}</h1>

        <div class="meta-pills">
          <span class="pill">📅 ${safeDate}</span>
          ${safeLocation ? `<span class="pill">📍 ${safeLocation}</span>` : ""}
          <span class="pill">👤 ${safeOwner}</span>
        </div>

        <div class="narrative">
          ${safeDescription || "<p><i>No description provided for this memory record.</i></p>"}
        </div>

        ${
          mediaImagesHtml
            ? `
          <div class="media-heading">Preserved Media Artifacts (${mediaList.length})</div>
          <div class="gallery-grid">
            ${mediaImagesHtml}
          </div>
        `
            : ""
        }

        <div class="footer-stamp">
          <span>Exported on ${exportDate}</span>
          <span>Verified Record • Memories Platform</span>
        </div>
      </div>
    </body>
    </html>
  `;
}

/**
 * Exports a memory to a local PDF file.
 *
 * @param {MemoryModel} memory The memory object to export.
 * @returns {Promise<IExportPdfResult>} File URI and base filename.
 */
export async function exportMemoryToPdf(memory: MemoryModel): Promise<IExportPdfResult> {
  const html = buildMemoryPdfHtml(memory);
  const cleanTitle = (memory.title || "memory").replace(/[^a-zA-Z0-9_-]/g, "_").toLowerCase();
  const fileName = `${cleanTitle}.pdf`;

  const { uri } = await Print.printToFileAsync({
    html,
  });

  return { uri, fileName };
}

/**
 * Dispatches a Memory via the device's native "Send To" / share sheet.
 * Renders the memory to an archival PDF and presents the OS share sheet.
 *
 * @param {MemoryModel} memory The memory item to send.
 * @returns {Promise<void>}
 */
export async function sendMemoryTo(memory: MemoryModel): Promise<void> {
  try {
    if (Platform.OS === "web") {
      const html = buildMemoryPdfHtml(memory);
      // On web, trigger print/save to PDF dialog directly
      await Print.printAsync({ html });
      return;
    }

    // Native Android / iOS
    const { uri } = await exportMemoryToPdf(memory);
    const isSharingAvailable = await Sharing.isAvailableAsync();

    if (isSharingAvailable) {
      await Sharing.shareAsync(uri, {
        mimeType: "application/pdf",
        dialogTitle: `Send To: ${memory.title || "Memory"}`,
        UTI: "com.adobe.pdf",
      });
    } else {
      await Share.share({
        title: memory.title || "Memory",
        message: `Preserved Memory: ${memory.title}\n${memory.description || ""}`,
        url: uri,
      });
    }
  } catch (error: any) {
    console.error("[MemoryPdfExporter] Error sending memory to PDF share sheet:", error);
    Alert.alert("Send To Failed", error.message || "Could not export memory to PDF.");
  }
}

/**
 * Dispatches a Media item via the device's native "Send To" / share sheet.
 *
 * @param {MediaModel} media The media item to send.
 * @returns {Promise<void>}
 */
export async function sendMediaTo(media: MediaModel): Promise<void> {
  try {
    const title = media.title || "Media";
    const shareMessage = `${title}: ${media.downloadUrl}`;

    if (Platform.OS !== "web" && (await Sharing.isAvailableAsync()) && media.downloadUrl) {
      await Share.share({
        title,
        message: shareMessage,
        url: media.downloadUrl,
      });
    } else {
      await Share.share({
        title,
        message: shareMessage,
        url: media.downloadUrl,
      });
    }
  } catch (error: any) {
    console.error("[MediaShare] Error in sendMediaTo:", error);
    Alert.alert("Send To Failed", error.message || "Could not send media.");
  }
}
